/**
 * confirm-dialog.ts
 * Modal de confirmación nativo de la app. Reemplaza window.confirm() para
 * garantizar compatibilidad cross-browser (Chrome, Brave, Safari) sin depender
 * de los diálogos nativos del navegador, que Chrome puede suprimir.
 */

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' tints the confirm button in red. Default: 'primary' */
  variant?: 'danger' | 'primary';
}

const DIALOG_ID = 'app-confirm-dialog';

/**
 * Muestra un modal de confirmación y retorna una Promise<boolean>.
 * true  → el usuario confirmó
 * false → el usuario canceló (o cerró el modal)
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  const {
    title = '¿Estás seguro?',
    message,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    variant = 'primary',
  } = options;

  return new Promise<boolean>((resolve) => {
    // Remove any previous instance
    document.getElementById(DIALOG_ID)?.remove();

    const overlay = document.createElement('div');
    overlay.id = DIALOG_ID;
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:9999',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:rgba(0,0,0,0.65)',
      'backdrop-filter:blur(4px)',
      'padding:1rem',
      'animation:confirmFadeIn 0.15s ease',
    ].join(';');

    const isDanger = variant === 'danger';

    overlay.innerHTML = `
      <style>
        @keyframes confirmFadeIn { from { opacity:0; transform:scale(0.97); } to { opacity:1; transform:scale(1); } }
        #${DIALOG_ID} .cd-box {
          background: var(--bg-card, #1a1a2e);
          border: 1px solid var(--border-metallic, rgba(255,255,255,0.12));
          border-radius: var(--radius-md, 12px);
          padding: 2rem;
          max-width: 420px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }
        #${DIALOG_ID} .cd-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--text-main, #f1f1f1);
          margin: 0;
        }
        #${DIALOG_ID} .cd-message {
          font-size: 0.93rem;
          color: var(--text-muted, #a0a0b0);
          line-height: 1.6;
          margin: 0;
        }
        #${DIALOG_ID} .cd-actions {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          flex-wrap: wrap;
        }
        #${DIALOG_ID} .cd-cancel {
          padding: 0.6rem 1.25rem;
          border-radius: var(--radius-sm, 8px);
          border: 1px solid var(--border-metallic, rgba(255,255,255,0.15));
          background: transparent;
          color: var(--text-muted, #a0a0b0);
          font-size: 0.9rem;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        #${DIALOG_ID} .cd-cancel:hover {
          background: rgba(255,255,255,0.06);
          color: var(--text-main, #f1f1f1);
        }
        #${DIALOG_ID} .cd-confirm {
          padding: 0.6rem 1.25rem;
          border-radius: var(--radius-sm, 8px);
          border: none;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s;
          background: ${isDanger ? 'rgba(239,68,68,0.85)' : 'var(--color-primary, #6366f1)'};
          color: #fff;
        }
        #${DIALOG_ID} .cd-confirm:hover { opacity: 0.85; }
      </style>
      <div class="cd-box" role="dialog" aria-modal="true" aria-labelledby="cd-title-text">
        <p class="cd-title" id="cd-title-text">${escapeHtml(title)}</p>
        <p class="cd-message">${escapeHtml(message)}</p>
        <div class="cd-actions">
          <button class="cd-cancel" id="cd-cancel-btn" type="button">${escapeHtml(cancelLabel)}</button>
          <button class="cd-confirm" id="cd-confirm-btn" type="button">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;

    const close = (result: boolean) => {
      overlay.remove();
      resolve(result);
    };

    overlay.querySelector('#cd-confirm-btn')!.addEventListener('click', () => close(true));
    overlay.querySelector('#cd-cancel-btn')!.addEventListener('click', () => close(false));

    // Click fuera del box → cancelar
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close(false);
    });

    // Escape → cancelar
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', onKeyDown);
        close(false);
      }
      if (e.key === 'Enter') {
        document.removeEventListener('keydown', onKeyDown);
        close(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);

    document.body.appendChild(overlay);
    // Focus en el botón de confirmación para accesibilidad
    (overlay.querySelector('#cd-confirm-btn') as HTMLButtonElement)?.focus();
  });
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
